import { ValueByPowerType, ValueByStorageType } from "../interface"
import { get_sim_state_by_h3r4_id } from "./get_sim_state_by_h3r4_id"
import { ModelDataInputs, SimulationState, StateAtTimepoint } from "./interface"



export function calculate_model_state_at_timepoint(
    simulation_state: SimulationState,
    data_dependencies: ModelDataInputs,
): StateAtTimepoint
{
    // At each moment, the model wants to calculate:
    // * how much energy was generated by each type of source by each cell since the
    //   last timestep
    //     * how much electricity is being generated by each aggregated cell of wind
    //       and solar power plants.
    // * how much electricity is being consumed by the demand in each aggregated cell
    //
    // This then enables:
    // * each cell to show an indicator when X energy has been generated by a source
    //     e.g. there might be wind, gas, nuclear, solar, and battery storage in one
    //          cell and these will generate different amounts of electricity at
    //          different times.
    // * also enables the model to calculate a redimentary electricity grid supply/demand
    //   balance for each cell.
    // * this enables showing cells which are lacking power supply
    // * and showing cells which are generating excess power supply and being
    //   curtailed
    // * when the marginal electricity pricing is modelled we can also show which
    //   cells are costing money because NESO asks it to reverse its bid.

    const datetime_diff_hourly_index = simulation_state.timesteps.length
    const datetime_annual_hourly_index = simulation_state.config.start_timestamp_index + datetime_diff_hourly_index

    // console.log("Updating model power supply for datetime index", datetime_annual_hourly_index)

    const demand_GW = get_demand_GW(
        data_dependencies.capacity_factor_data.wind.date_time_to_index.size,
        data_dependencies.demand_GW_by_hour.data,
        datetime_annual_hourly_index,
    )

    const sim_state_by_h3r4_id = get_sim_state_by_h3r4_id(
        demand_GW,
        data_dependencies.initial_proportional_electricity_demand_GW_by_h3r4,
        data_dependencies.capacity_factor_data,
        simulation_state.built_power_plants.aggregated_by_h3r4,
        simulation_state.timesteps.last()?.by_h3r4,
        datetime_annual_hourly_index,
    )

    const generated_GW_by_type: ValueByPowerType<number> = {
        wind: 0,
        solar: 0,
        gas: 0,
        nuclear: 0,
        battery: 0,
        hydro_river: 0,
        hydro_pumped_storage: 0,
    }
    const stored_GWh_by_type: ValueByStorageType<number> = {
        battery: 0,
        hydro_pumped_storage: 0,
    }


    const total_MW = { primary: 0, storage: 0 }
    Object.values(sim_state_by_h3r4_id).forEach(c =>
    {
        total_MW.primary += c.power_plants?.total_generated_MW.primary ?? 0
        total_MW.storage += c.power_plants?.total_generated_MW.storage ?? 0

        generated_GW_by_type.wind += (c.power_plants?.wind.generated_MW ?? 0) / 1000
        generated_GW_by_type.solar += (c.power_plants?.solar.generated_MW ?? 0) / 1000
        generated_GW_by_type.gas += (c.power_plants?.gas.generated_MW ?? 0) / 1000
        generated_GW_by_type.nuclear += (c.power_plants?.nuclear.generated_MW ?? 0) / 1000
        generated_GW_by_type.hydro_river += (c.power_plants?.hydro_river.generated_MW ?? 0) / 1000
        generated_GW_by_type.battery += (c.power_plants?.battery.generated_MW ?? 0) / 1000
        generated_GW_by_type.hydro_pumped_storage += (c.power_plants?.hydro_pumped_storage.generated_MW ?? 0) / 1000

        stored_GWh_by_type.battery += (c.power_plants?.battery.stored_MWh ?? 0) / 1000
        stored_GWh_by_type.hydro_pumped_storage += (c.power_plants?.hydro_pumped_storage.stored_MWh ?? 0) / 1000
    })


    const generated_GW = {
        primary: total_MW.primary / 1000,
        storage: total_MW.storage / 1000,
        total_positive: 0,
    }
    generated_GW.total_positive = generated_GW.primary + Math.max(generated_GW.storage, 0)

    const excess_GW = Math.max(0, generated_GW.primary + generated_GW.storage - demand_GW)

    const timestamp_ms = simulation_state.config.start_datetime.getTime() + (datetime_diff_hourly_index * 60 * 60 * 1000)

    const state_at_timepoint: StateAtTimepoint = {
        timestamp_ms,

        demand_GW,
        by_h3r4: sim_state_by_h3r4_id,

        generated_GW_by_type,
        stored_GWh_by_type,

        generated_GW,

        excess_GW,
        flipflop_GW: 0,
    }

    return state_at_timepoint
}


function get_demand_GW(time_size: number, UK_demand_GW_by_hour: number[], datetime_index: number): number
{
    const demand_GW = UK_demand_GW_by_hour[datetime_index % time_size] ?? 0
    return demand_GW
}
